import { EdgerRollPass } from "@/pages/catalogs/models/EdgerRoll";
import {
	getPassColor,
	PassCircle,
} from "@/pages/catalogs/pages/edger-rolls/components/passes/PassesGrid";
import { Visibility } from "@mui/icons-material";
import {
	alpha,
	Box,
	Chip,
	FormControl,
	FormControlLabel,
	IconButton,
	Radio,
	RadioGroup,
	Stack,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import { Link } from "react-router-dom";
import { rollPositionCodes } from "./RollPositionSelector";

export const isPassDisabled = (
	passNumber: number,
	passes: { top: EdgerRollPass[] | null; bottom: EdgerRollPass[] | null },
	sectionName: string | null,
): boolean => {
	const topPass = passes.top?.find((p) => p.PassNumber === passNumber);
	const bottomPass = passes.bottom?.find((p) => p.PassNumber === passNumber);

	const topSections = topPass?.Sections.map((s) => s.SectionName) ?? [];
	const bottomSections = bottomPass?.Sections.map((s) => s.SectionName) ?? [];
	const commonSections = topSections.filter((s) => bottomSections.includes(s));

	return (
		topPass?.PassStatusCode === "D" ||
		bottomPass?.PassStatusCode === "D" ||
		!topPass?.PassStatusCode ||
		!bottomPass?.PassStatusCode ||
		!commonSections.includes(sectionName!)
	);
};

const getPassTooltip = (
	statusArray: string[],
	commonSections: string[],
	sectionName: string | null,
) => {
	const [first, second] = statusArray;

	if (!first && !second)
		return "Pass does not exist for Top Roll and Bottom Roll";
	if (!first) return "Pass does not exist for Top Roll";
	if (!second) return "Pass does not exist for Bottom Roll";

	if (first === "D" && second === "D") return "Pass 'Damaged' for both rolls";
	if (first === "D") return "Pass 'Damaged' for Top Roll";
	if (second === "D") return "Pass 'Damaged' for Bottom Roll";

	const incompatible =
		sectionName && !commonSections.includes(sectionName)
			? `. Can't be selectable because isn't compatible with ${sectionName}`
			: "";

	if (first === "R" && second === "R")
		return `Pass 'Ready' for both rolls${incompatible}`;
	if (first === "R" && second === "U")
		return `Pass 'Ready' for Top Roll and 'Used' for Bottom Roll${incompatible}`;
	if (first === "U" && second === "R")
		return `Pass 'Used' for Top Roll and 'Ready' for Bottom Roll${incompatible}`;

	return "";
};

const getDisableMessage = (
	statusArray: string[],
	commonSections: string[],
	sectionName: string | null,
) => {
	const [first, second] = statusArray;
	let message;

	if (!first && !second)
		message = "pass does not exist for Top Roll and Bottom Roll";
	if (!first) message = "pass does not exist for Top Roll";
	if (!second) message = "pass does not exist for Bottom Roll";

	if (first === "D" && second === "D")
		message = "pass was 'Damaged' for both rolls";
	if (first === "D") message = "pass was 'Damaged' for Top Roll";
	if (second === "D") message = "pass was 'Damaged' for Bottom Roll";

	if (message) {
		return ` (Can't be selectable because ${message})`;
	}

	return sectionName && !commonSections.includes(sectionName)
		? ` (Can't be selectable because isn't compatible with ${sectionName})`
		: "";
};

const getDiagonalState = (
	topPass: EdgerRollPass | undefined,
	bottomPass: EdgerRollPass | undefined,
): "none" | "both" | "top" | "bottom" => {
	if (topPass?.IsDiagonal && bottomPass?.IsDiagonal) return "both";
	if (topPass?.IsDiagonal) return "top";
	if (bottomPass?.IsDiagonal) return "bottom";
	return "none";
};

const DiagonalChip = ({
	position,
	visible,
}: {
	position: "T" | "B";
	visible: boolean;
}) => {
	const color = rollPositionCodes.find((p) => p.Code === position)?.Color ?? "";

	return (
		<Chip
			size="small"
			label={`${position} · DIAGONAL`}
			aria-hidden={!visible}
			sx={{
				height: 16,
				fontSize: "0.55rem",
				fontWeight: "bold",
				backgroundColor: alpha(color, 0.8),
				border: `1px solid ${color}`,
				color: "text.primary",
				visibility: visible ? "visible" : "hidden",
				"& .MuiChip-label": { px: 0.75 },
			}}
		/>
	);
};

const getDiagonalTooltip = (state: "none" | "both" | "top" | "bottom") => {
	if (state === "both") return "Diagonal pass for Top Roll and Bottom Roll";
	if (state === "top") return "Diagonal pass only for Top Roll";
	if (state === "bottom") return "Diagonal pass only for Bottom Roll";
	return "";
};

type EdgerPassSelectorProps = {
	value: number | null;
	onChange: (value: number | null) => void;
	withShortcut?: boolean;
	passes: { top: EdgerRollPass[] | null; bottom: EdgerRollPass[] | null };
	rollNames: (string | null | undefined)[];
	rollIds: (number | null | undefined)[];
	sectionName: string | null;
	disabled?: boolean;
	horizontal?: boolean;
};

export const EdgerPassSelector = ({
	value,
	onChange,
	withShortcut = false,
	passes,
	rollNames,
	rollIds,
	sectionName,
	disabled = false,
	horizontal = false,
}: EdgerPassSelectorProps) => {
	const theme = useTheme();

	if (!passes.top || !passes.bottom) {
		return (
			<Typography variant="body2" color="textSecondary">
				No passes available
			</Typography>
		);
	}

	const maxPassNumber = Math.max(
		...(passes.top?.map((p) => p.PassNumber) ?? [0]),
		...(passes.bottom?.map((p) => p.PassNumber) ?? [0]),
	);

	const passNumbers = Array.from({ length: maxPassNumber }, (_, i) => i + 1);

	return (
		<FormControl component="fieldset" fullWidth>
			{rollNames && (
				<Box display="flex" alignItems="center" gap={6} marginBottom={1}>
					{withShortcut && (
						<>
							<Box
								display="flex"
								flexDirection="column"
								alignItems="flex-start"
							>
								<Box display="flex" alignItems="center" gap={0.5}>
									<Box
										sx={{
											width: 8,
											height: 8,
											borderRadius: "50%",
											backgroundColor: rollPositionCodes.find(
												(p) => p.Code === "T",
											)?.Color,
										}}
									/>
									<Typography
										variant="caption"
										color="textSecondary"
										fontWeight="bold"
									>
										Top Roll
									</Typography>
								</Box>
								<Box display="flex" alignItems="center" gap={0.5}>
									<IconButton
										component={Link}
										to={`/catalogs/edger-rolls/${rollIds[0]}`}
										target="_blank"
										size="small"
										title="See in edger rolls"
									>
										<Visibility fontSize="small" />
									</IconButton>
									<Typography variant="body2" color="textSecondary">
										{rollNames[0]}
									</Typography>
								</Box>
							</Box>

							<Box
								display="flex"
								flexDirection="column"
								alignItems="flex-start"
							>
								<Box display="flex" alignItems="center" gap={0.5}>
									<Box
										sx={{
											width: 8,
											height: 8,
											borderRadius: "50%",
											backgroundColor: rollPositionCodes.find(
												(p) => p.Code === "B",
											)?.Color,
										}}
									/>
									<Typography
										variant="caption"
										color="textSecondary"
										fontWeight="bold"
									>
										Bottom Roll
									</Typography>
								</Box>
								<Box display="flex" alignItems="center" gap={0.5}>
									<IconButton
										component={Link}
										to={`/catalogs/edger-rolls/${rollIds[1]}`}
										target="_blank"
										size="small"
										title="See in edger rolls"
									>
										<Visibility fontSize="small" />
									</IconButton>
									<Typography variant="body2" color="textSecondary">
										{rollNames[1]}
									</Typography>
								</Box>
							</Box>
						</>
					)}
				</Box>
			)}

			<RadioGroup
				value={value?.toString() || ""}
				onChange={(e) => onChange(parseInt(e.target.value, 10))}
				row={horizontal}
			>
				{passNumbers.map((passNumber) => {
					const topPass = passes.top?.find((p) => p.PassNumber === passNumber);
					const bottomPass = passes.bottom?.find(
						(p) => p.PassNumber === passNumber,
					);

					const topSections = topPass?.Sections.map((s) => s.SectionName) ?? [];
					const bottomSections =
						bottomPass?.Sections.map((s) => s.SectionName) ?? [];
					const commonSections = topSections.filter((s) =>
						bottomSections.includes(s),
					);

					const diagonalState = getDiagonalState(topPass, bottomPass);
					const tooltipTitle = [
						getPassTooltip(
							[topPass?.PassStatusCode!, bottomPass?.PassStatusCode!],
							commonSections,
							sectionName,
						),
						getDiagonalTooltip(diagonalState),
					]
						.filter(Boolean)
						.join(" · ");

					return (
						<FormControlLabel
							key={passNumber}
							value={passNumber.toString()}
							control={<Radio disabled={disabled} color="primary" />}
							disabled={
								disabled || isPassDisabled(passNumber, passes, sectionName)
							}
							data-test-id={`pass-${passNumber}`}
							label={
								<Tooltip title={tooltipTitle}>
									<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										<span style={{ display: "flex" }}>
											<PassCircle
												sx={{ fontSize: 14 }}
												firstColor={
													getPassColor(topPass?.PassStatusCode!, theme).main
												}
												secondColor={
													getPassColor(bottomPass?.PassStatusCode!, theme).main
												}
												isDiagonal={diagonalState !== "none"}
											/>
										</span>
										<Stack>
											<Typography variant="body2" lineHeight={1}>
												{`Pass ${passNumber}`}
												{horizontal
													? null
													: `${getDisableMessage(
															[
																topPass?.PassStatusCode!,
																bottomPass?.PassStatusCode!,
															],
															commonSections,
															sectionName,
														)}`}
											</Typography>
										</Stack>
										{diagonalState !== "none" && (
											<Stack spacing={0.25} alignItems="flex-start">
												<DiagonalChip
													position="T"
													visible={!!topPass?.IsDiagonal}
												/>
												<DiagonalChip
													position="B"
													visible={!!bottomPass?.IsDiagonal}
												/>
											</Stack>
										)}
									</Box>
								</Tooltip>
							}
						/>
					);
				})}
			</RadioGroup>
		</FormControl>
	);
};
